/**
 * Guided View Copy Matrix v3.3 • NAV CONSOLIDATION + USING GRANULLO
 * 
 * ROLE-AWARE PERSONA VISIBILITY:
 * - CLIENT users: Only CLIENT persona (no confusion)
 * - BANK users: BANK + CLIENT personas
 * - RBI/REGULATOR users: RBI + BANK + CLIENT personas
 * - SUPER_ADMIN: All personas including DEVELOPER
 * 
 * v3.3 UPDATES (NAV CONSOLIDATION + USING GRANULLO):
 * - CSR is now a consolidated group under Governance & Trust
 * - Foundation Invites moved to CSR Partners as a tab
 * - Impact Intelligence moved to platform-wide Insights
 * - NEW: Using Granullo hub page (/using-granullo)
 * - Updated step references for new navigation structure
 * 
 * 24-STEP OPERATIONAL + TRUST + INTEGRATIONS + CSR FLOW:
 * 1. Dashboard
 * 2. Programs • Create Program CTA
 * 3. Program Builder • rules, wallets, constraints
 * 4. Beneficiaries • add + bulk upload
 * 5. Merchants • add, categories, eligibility
 * 6. Policies
 * 7. Approvals
 * 8. Bank Requests
 * 9. Review & Clarifications
 * 10. Compliance + Audit Logs
 * 11. Trust Layer Pilot (role-gated)
 * 12. Executive Trust Dashboard (role-gated)
 * 13. Enterprise Integrations (role-gated)
 * 14. CSR Programs (v3.0) • Corporate creates programs
 * 15. CSR Partners (v3.3) • Foundation onboarding + Invites tab
 * 16. CSR Allocations (role-gated) • Health Signals, Refunds
 * 17. CSR Communication (v3.0) • Foundation ↔ Client notes
 * 18. CSR Merchant Governance (v3.0) • Donor veto
 * 19. Why CSR Breaks (v3.1) • Governance Map (now under CSR > Governance)
 * 20. CSR Analytics (v3.1) • Execution Dashboard (now under Insights)
 * 21. Payment Layer (v3.1) • Immutable Ledger
 * 22. Impact Intelligence (v3.2) • READ-ONLY Context Module (now under Insights)
 * 23. Using Granullo (v3.3) • In-Product Help Hub
 * 24. CSR Start (v3.3) • Hub entry point
 * 
 * v3.2 UPDATES (IMPACT INTELLIGENCE):
 * - Impact Intelligence: Read-only, non-opinionated cause exploration
 * - "Context before Commitment" • Inform decisions, never recommend
 * - Active Alerts: Situational awareness without urgency CTAs
 * - Factual Snippets: Source-attributed data points
 * 
 * v3.1 UPDATES (CSR EXECUTION HARDENING):
 * - Why CSR Breaks: Governance map showing failure modes
 * - CSR Analytics: Execution-focused dashboard for CFO/Auditor/Regulator
 * - Foundation Invites: Corporate invites trusted foundations
 * - Payment Layer: Demo/operational modes, immutable ledger
 * - Document Clarification: Blocking logic for compliance
 * - Auto-refund: Deadline tracking and countdown
 * 
 * DEVELOPER GUIDED VIEW (separate flow, gated):
 * - API Keys lifecycle
 * - Webhooks event flow
 * - Signing & verification
 * - Integration audit logs
 */

export const PERSONAS = {
  RBI: 'rbi',
  BANK: 'bank',
  CLIENT: 'client',
  DEVELOPER: 'developer'
};

export const PERSONA_LABELS = {
  [PERSONAS.RBI]: 'RBI / Regulator',
  [PERSONAS.BANK]: 'Bank Partner',
  [PERSONAS.CLIENT]: 'Client / Donor',
  [PERSONAS.DEVELOPER]: 'Developer / Technical'
};

export const PERSONA_DESCRIPTIONS = {
  [PERSONAS.RBI]: 'Regulatory oversight lens • controls, auditability, compliance',
  [PERSONAS.BANK]: 'Operational workflow lens • execution, risk, integration',
  [PERSONAS.CLIENT]: 'Intent and trust lens • clarity, brand, beneficiary outcomes',
  [PERSONAS.DEVELOPER]: 'Technical integration lens • APIs, webhooks, security'
};

/**
 * ROLE-AWARE PERSONA VISIBILITY
 * Returns only the personas a user role can access
 * Server-authoritative: role determines visibility, not user choice
 */
export const getPersonasForRole = (userRole) => {
  switch (userRole) {
    case 'super_admin':
      return [PERSONAS.RBI, PERSONAS.BANK, PERSONAS.CLIENT, PERSONAS.DEVELOPER];
    case 'regulator':
    case 'auditor':
      return [PERSONAS.RBI, PERSONAS.BANK, PERSONAS.CLIENT];
    case 'bank_partner':
    case 'bank_admin':
    case 'bank_tech':
      return [PERSONAS.BANK, PERSONAS.CLIENT];
    case 'client_admin':
    case 'finance_ops':
    case 'donor':
    default:
      return [PERSONAS.CLIENT];
  }
};

/**
 * Get the default persona for a role
 */
export const getDefaultPersonaForRole = (userRole) => {
  switch (userRole) {
    case 'regulator':
    case 'auditor':
      return PERSONAS.RBI;
    case 'bank_partner':
    case 'bank_admin':
    case 'bank_tech':
      return PERSONAS.BANK;
    case 'super_admin':
      return PERSONAS.CLIENT; // Start with client view, can switch
    default:
      return PERSONAS.CLIENT;
  }
};

/**
 * Check if a user role can access a specific persona
 */
export const canAccessPersona = (userRole, persona) => {
  const allowedPersonas = getPersonasForRole(userRole);
  return allowedPersonas.includes(persona);
};

/**
 * Check if user should see persona selector (more than one option)
 */
export const shouldShowPersonaSelector = (userRole) => {
  const allowedPersonas = getPersonasForRole(userRole);
  return allowedPersonas.length > 1;
};

/**
 * 10-Step Guided Copy Matrix with persona-specific narratives
 */
export const GUIDED_COPY_MATRIX = {
  dashboard: {
    id: 'dashboard',
    targetPath: '/dashboard',
    navItem: 'Dashboard',
    requiredRoles: null, // All roles
    copy: {
      [PERSONAS.RBI]: {
        title: 'Governance Command Center',
        what: 'Central visibility into all program activity, pending governance actions, and system health.',
        why: 'Ensures no operations occur without oversight. Every metric shown is audit-backed.',
        who: 'Regulators and auditors verify operational transparency at a glance.',
        next: 'Drill into any metric to see underlying transactions and approval chains.'
      },
      [PERSONAS.BANK]: {
        title: 'Operations Dashboard',
        what: 'Real-time view of escrow activity, pending execution requests, and program status.',
        why: 'Enables proactive risk management and SLA tracking for execution requests.',
        who: 'Bank operations teams monitor for incoming requests and exception alerts.',
        next: 'Click any pending item to review details and begin execution workflow.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Program Overview',
        what: 'Your single view of program health, disbursement progress, and beneficiary outcomes.',
        why: 'Provides clarity on how funds are being used and what actions need attention.',
        who: 'Program administrators and donors track impact and governance status.',
        next: 'Review pending approvals or explore program details from the metrics cards.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Integration Health Dashboard',
        what: 'System status, API health, webhook delivery rates, and integration metrics.',
        why: 'Monitor integration stability and identify issues before they impact operations.',
        who: 'Technical teams track API performance and webhook reliability.',
        next: 'Check API status and review recent integration events.'
      }
    }
  },

  programs: {
    id: 'programs',
    targetPath: '/programs',
    navItem: 'Programs',
    requiredRoles: ['super_admin', 'client_admin', 'finance_ops'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'Program Registry',
        what: 'All disbursement programs registered on the platform with governance status.',
        why: 'Single source of truth for program-level controls and budget allocations.',
        who: 'Regulators verify program configurations and governance guardrails.',
        next: 'Expand any program to inspect rules, budgets, and compliance status.'
      },
      [PERSONAS.BANK]: {
        title: 'Client Programs',
        what: 'Programs from partner organizations that generate execution requests.',
        why: 'Understanding program structure helps anticipate request volume and types.',
        who: 'Bank partners review to align operational capacity with client needs.',
        next: 'Review active programs to understand incoming request patterns.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Your Programs',
        what: 'Where you create and manage disbursement programs for your organization.',
        why: 'Each program defines a distinct fund flow with its own rules and recipients.',
        who: 'Program admins create programs; bank partners approve them before launch.',
        next: 'Click "Create New Program" to set up a new disbursement flow.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Program API Objects',
        what: 'Programs as API resources with IDs, configurations, and webhook subscriptions.',
        why: 'Understanding program structure is essential for API integration.',
        who: 'Developers reference program IDs when building integrations.',
        next: 'Note program IDs for use in API calls and webhook filtering.'
      }
    }
  },

  'program-builder': {
    id: 'program-builder',
    targetPath: '/create',
    navItem: 'Program Builder',
    requiredRoles: ['super_admin', 'client_admin'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'Program Configuration Controls',
        what: 'Where program parameters, budgets, and disbursement rules are defined.',
        why: 'Evidence of governance-by-design. All constraints are explicit and auditable.',
        who: 'Regulators examine configurations to verify proper controls exist.',
        next: 'Review how budget limits, approval thresholds, and rules are configured.'
      },
      [PERSONAS.BANK]: {
        title: 'Program Structure Review',
        what: 'The configuration interface where clients define program parameters.',
        why: 'Helps anticipate what types of requests this program will generate.',
        who: 'Bank partners review configurations before approving new programs.',
        next: 'Note the budget allocation, disbursement frequency, and rule complexity.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Build Your Program',
        what: 'Define your program name, budget, disbursement rules, and constraints.',
        why: 'This is where you translate your fund intent into executable governance.',
        who: 'You configure; bank partner reviews and approves before activation.',
        next: 'Enter program details, set budget limits, and define disbursement rules.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Program Configuration Schema',
        what: 'The data model and validation rules for program creation via API.',
        why: 'Understanding the schema enables programmatic program management.',
        who: 'Developers building program automation or bulk creation tools.',
        next: 'Review required fields and validation rules for the Programs API.'
      }
    }
  },

  beneficiaries: {
    id: 'beneficiaries',
    targetPath: '/beneficiaries',
    navItem: 'Beneficiaries',
    requiredRoles: ['super_admin', 'client_admin', 'finance_ops'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'Beneficiary Registry',
        what: 'All individuals and entities eligible to receive disbursements.',
        why: 'Evidence of proper recipient verification and KYC compliance.',
        who: 'Auditors verify beneficiary eligibility and documentation status.',
        next: 'Expand any beneficiary to see verification documents and disbursement history.'
      },
      [PERSONAS.BANK]: {
        title: 'Recipient Database',
        what: 'Verified beneficiaries whose accounts receive disbursement credits.',
        why: 'Understanding the recipient pool helps assess execution risk.',
        who: 'Bank compliance reviews beneficiary verification status.',
        next: 'Review KYC status and account verification for compliance assessment.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Your Beneficiaries',
        what: 'The people and organizations who will receive funds from your programs.',
        why: 'Adding beneficiaries is step one in setting up any disbursement flow.',
        who: 'You add and manage beneficiaries; platform verifies their details.',
        next: 'Click "Add Beneficiary" or use bulk upload for multiple recipients.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Beneficiary API Resources',
        what: 'Beneficiary records as API objects with verification status and metadata.',
        why: 'Bulk beneficiary management often requires API integration.',
        who: 'Developers building beneficiary import/export or sync tools.',
        next: 'Review the Beneficiaries API for bulk operations and webhooks.'
      }
    }
  },

  merchants: {
    id: 'merchants',
    targetPath: '/merchants',
    navItem: 'Merchants',
    requiredRoles: ['super_admin', 'client_admin', 'finance_ops'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'Merchant Whitelist Registry',
        what: 'Approved merchants where beneficiaries can spend disbursed funds.',
        why: 'Evidence of spend controls. Funds can only flow to pre-approved destinations.',
        who: 'Regulators verify merchant category restrictions and approval workflows.',
        next: 'Review merchant categories and approval status for compliance verification.'
      },
      [PERSONAS.BANK]: {
        title: 'Authorized Merchants',
        what: 'Merchant network where program funds can be spent.',
        why: 'Merchant restrictions are part of the governance that precedes execution.',
        who: 'Bank partners understand spend constraints before processing requests.',
        next: 'Review which merchant categories are enabled for different programs.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Spending Destinations',
        what: 'Define where your beneficiaries can spend the funds they receive.',
        why: 'Merchant controls ensure funds are used for intended purposes.',
        who: 'You configure merchant categories; policies enforce spend restrictions.',
        next: 'Add approved merchants or configure category-level restrictions.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Merchant Category Controls',
        what: 'MCC-based restrictions and merchant whitelisting via API.',
        why: 'Spend controls are often managed programmatically at scale.',
        who: 'Developers integrating with POS or payment systems.',
        next: 'Review the Merchants API for category management.'
      }
    }
  },

  policies: {
    id: 'policies',
    targetPath: '/policies',
    navItem: 'Policies',
    requiredRoles: null,
    copy: {
      [PERSONAS.RBI]: {
        title: 'Programmable Governance Rules',
        what: 'Policy definitions that constrain all disbursements. Rules are code, not discretion.',
        why: 'Demonstrates governance-by-design. Policies are versioned and immutable once activated.',
        who: 'Regulators examine policy configurations to verify rule determinism.',
        next: 'Expand any policy to inspect rule logic, version history, and approval chain.'
      },
      [PERSONAS.BANK]: {
        title: 'Disbursement Rule Configurations',
        what: 'Policy rules that govern what requests your institution will receive.',
        why: 'Understanding policies helps anticipate request patterns and exceptions.',
        who: 'Bank partners review policies to align internal execution procedures.',
        next: 'Review amount limits and conditions that trigger bank involvement.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Program Rules & Controls',
        what: 'The rules that define who can receive funds and under what conditions.',
        why: 'Ensures your disbursements align with program intent and prevent misuse.',
        who: 'Program admins create and manage policies; bank partners approve them.',
        next: 'Create new policies or review existing rules for your program design.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Policy Engine API',
        what: 'Policy rules as programmable constraints with versioning and audit trails.',
        why: 'Complex policy management often requires API-driven automation.',
        who: 'Developers building policy management or simulation tools.',
        next: 'Review the Policy API for rule creation and version management.'
      }
    }
  },

  approvals: {
    id: 'approvals',
    targetPath: '/approvals',
    navItem: 'Approvals',
    requiredRoles: null,
    copy: {
      [PERSONAS.RBI]: {
        title: 'Human Oversight Checkpoints',
        what: 'Approval queue showing decisions that require human judgment.',
        why: 'Evidence of separation of duties. Every approval is logged with attribution.',
        who: 'Auditors verify critical decisions flow through appropriate chains.',
        next: 'Expand any item to see full approval history and decision rationale.'
      },
      [PERSONAS.BANK]: {
        title: 'Pending Decision Queue',
        what: 'Requests awaiting approval before proceeding to bank execution.',
        why: 'Upstream approval status determines what will arrive in your queue.',
        who: 'Bank partners monitor to anticipate incoming execution requests.',
        next: 'Review high-priority items that may require expedited processing.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Your Approval Inbox',
        what: 'Items requiring your decision • budget changes, policy exceptions, and more.',
        why: 'You maintain control while Granullo provides governance rails.',
        who: 'Program admins approve or reject requests with full audit trail.',
        next: 'Review each item, add comments if needed, and make your decision.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Approval Workflow API',
        what: 'Approval states, transitions, and webhook events for workflow integration.',
        why: 'Approval automation is common for high-volume operations.',
        who: 'Developers building approval routing or notification systems.',
        next: 'Review approval webhooks for real-time status updates.'
      }
    }
  },

  'bank-requests': {
    id: 'bank-requests',
    targetPath: '/settings/governance/bank-requests',
    navItem: 'Bank Requests',
    requiredRoles: null,
    copy: {
      [PERSONAS.RBI]: {
        title: 'Execution Boundary',
        what: 'The handoff point where Granullo instructions become bank execution requests.',
        why: 'Clear audit boundary. Granullo instructs; banks execute. Separation is sacrosanct.',
        who: 'Regulators verify execution responsibility is clearly demarcated.',
        next: 'Inspect request status to confirm execution completion and timing.'
      },
      [PERSONAS.BANK]: {
        title: 'Incoming Execution Queue',
        what: 'Your primary work queue • approved requests ready for bank processing.',
        why: 'Each request has been through Granullo governance. Your job is compliant execution.',
        who: 'Bank operations teams process these against your internal controls.',
        next: 'Select a request to begin execution workflow or update status.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Bank Processing Status',
        what: 'Track where your approved disbursements are in the bank pipeline.',
        why: 'Visibility into the final mile. Know when funds reach beneficiaries.',
        who: 'Program admins monitor for execution delays or issues.',
        next: 'Contact your bank partner if requests remain pending beyond SLAs.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Bank Integration API',
        what: 'Execution request payloads, status callbacks, and reconciliation endpoints.',
        why: 'Bank integrations require precise API contracts and idempotency.',
        who: 'Developers building bank system integrations.',
        next: 'Review execution request schemas and callback specifications.'
      }
    }
  },

  'review-clarifications': {
    id: 'review-clarifications',
    targetPath: '/review-clarifications',
    navItem: 'Review & Clarifications',
    requiredRoles: null,
    copy: {
      [PERSONAS.RBI]: {
        title: 'Immutable Communication Record',
        what: 'Thread-based discussions where all messages and evidence are fingerprinted.',
        why: 'No Slack, no email chains. Every communication is auditable.',
        who: 'Auditors review communication history to verify governance discussions.',
        next: 'Open any thread to see full message chain and evidence attachments.'
      },
      [PERSONAS.BANK]: {
        title: 'Structured Clarification Threads',
        what: 'Formal communication channel with clients about pending requests.',
        why: 'Creates auditable record of all discussions. Attachments are fingerprinted.',
        who: 'Bank partners use this to request clarification or documentation.',
        next: 'Respond to pending threads or request evidence before approving.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Discussion & Evidence Threads',
        what: 'Communicate with your bank partner about policies, requests, and compliance.',
        why: 'Replaces email with auditable threads. Evidence submissions become permanent.',
        who: 'Program admins submit clarifications; bank partners respond.',
        next: 'Start a new thread for questions or add evidence to existing discussions.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Messaging & Evidence API',
        what: 'Thread creation, message posting, and evidence attachment endpoints.',
        why: 'Automated evidence submission and thread management at scale.',
        who: 'Developers building document management or communication integrations.',
        next: 'Review the Threads API and evidence attachment specifications.'
      }
    }
  },

  'compliance-audit': {
    id: 'compliance-audit',
    targetPath: '/compliance',
    navItem: 'Compliance & Audit',
    requiredRoles: null,
    copy: {
      [PERSONAS.RBI]: {
        title: 'Compliance Posture & Audit Trail',
        what: 'Real-time compliance status and complete, immutable activity logs.',
        why: 'The foundation of Granullo\'s trust model. Every action is logged forever.',
        who: 'This is your primary evidence source for audit and investigation.',
        next: 'Drill into compliance metrics or filter audit logs by event type.'
      },
      [PERSONAS.BANK]: {
        title: 'Partner Compliance & Execution Trail',
        what: 'Compliance health and full execution history for partner organizations.',
        why: 'Risk indicator for execution decisions. Non-compliant programs need scrutiny.',
        who: 'Bank compliance teams monitor before processing requests.',
        next: 'Review compliance warnings and search for specific transactions.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Your Compliance Health & Activity',
        what: 'How well your program adheres to governance requirements, plus full activity log.',
        why: 'Stay ahead of issues. Full transparency on who did what and when.',
        who: 'Program admins monitor compliance and review operational history.',
        next: 'Resolve compliance warnings and use filters to find specific events.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Audit Log API & Webhooks',
        what: 'Event streaming, audit log queries, and compliance status endpoints.',
        why: 'Compliance integration and real-time event processing.',
        who: 'Developers building SIEM integrations or compliance dashboards.',
        next: 'Review the Audit API and event webhook specifications.'
      }
    }
  },

  // === TRUST SURFACES (Steps 11-12) - ROLE-GATED ===
  
  'trust-pilot': {
    id: 'trust-pilot',
    targetPath: '/trust-pilot',
    navItem: 'Trust Layer Pilot',
    requiredRoles: ['super_admin', 'regulator', 'auditor'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'Trust Layer Pilot • Regulator Oversight',
        what: 'Your dedicated oversight surface for trust signals, exceptions, and audit readiness.',
        why: 'Answer instantly: Is this system under control? Can issues be detected and traced?',
        who: 'Regulators and auditors access this for compliance verification and audit prep.',
        next: 'Review trust signals, examine exceptions, and verify audit trail completeness.'
      },
      [PERSONAS.BANK]: {
        title: 'Trust Layer Pilot • Oversight Visibility',
        what: 'Read-only view of what regulators and auditors see when reviewing the platform.',
        why: 'Understand the oversight lens. Know what evidence is available for audits.',
        who: 'Bank compliance teams can preview the regulator experience.',
        next: 'Explore the trust signals and exception registry as regulators see them.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Trust Layer Pilot • What Oversight Looks Like',
        what: 'A read-only view of how regulators and banks monitor platform governance.',
        why: 'Transparency in how your programs are overseen. No hidden processes.',
        who: 'Available for transparency; clients do not perform actions here.',
        next: 'Review the oversight surface to understand governance visibility.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Trust Layer Pilot • Oversight APIs',
        what: 'Trust signals, exception registry, and audit readiness surfaced via read-only APIs.',
        why: 'Understand data structures for building oversight integrations.',
        who: 'Developers building compliance dashboards or audit automation.',
        next: 'Review the trust oversight API structure and response formats.'
      }
    }
  },

  'executive-dashboard': {
    id: 'executive-dashboard',
    targetPath: '/executive-dashboard',
    navItem: 'Executive Trust Dashboard',
    requiredRoles: ['super_admin', 'bank_partner', 'regulator'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'Executive Trust Dashboard • Confidence Signal',
        what: 'The single screen executives use to assess platform trust posture.',
        why: 'Demonstrates institutional-grade confidence communication to leadership.',
        who: 'Bank CXOs, regulators reviewing platform readiness.',
        next: 'Review how trust status, risks, and governance are presented to executives.'
      },
      [PERSONAS.BANK]: {
        title: 'Executive Trust Dashboard • Your Confidence View',
        what: 'Confidence snapshot: Do you trust this system today? System health, risks, governance.',
        why: 'Designed for executive briefings. No drilling required for basic confidence.',
        who: 'Bank CEOs, CROs, Compliance Heads • anyone needing instant trust assessment.',
        next: 'Generate a Daily Brief for offline review or stakeholder communication.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Executive Trust Dashboard • What Executives See',
        what: 'A read-only view of the confidence dashboard used by bank and regulatory leadership.',
        why: 'Understand how your program health contributes to platform trust posture.',
        who: 'View-only for transparency; executives make assessments here.',
        next: 'See how your program metrics roll up into executive-level visibility.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Executive Trust Dashboard • Data Architecture',
        what: 'System health, risk categories, governance snapshots, and audit readiness aggregated.',
        why: 'Reference architecture for building executive-facing integrations.',
        who: 'Developers building board-level reporting or executive dashboards.',
        next: 'Review the data aggregation patterns and API endpoints.'
      }
    }
  },

  'enterprise-integrations': {
    id: 'enterprise-integrations',
    targetPath: '/settings/integrations',
    navItem: 'Integrations',
    requiredRoles: ['super_admin', 'bank_tech', 'client_admin'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'Integration Governance',
        what: 'Enterprise API keys, webhooks, usage analytics, and operational recovery for system-to-system integrations.',
        why: 'All integrations are auditable. Key lifecycle, webhook events, usage metrics, and retry operations are logged and observable.',
        who: 'Security teams verify policies; auditors review access patterns, reliability metrics, and recovery actions.',
        next: 'Review API key audit logs, webhook health, and retry dashboard for compliance assurance.'
      },
      [PERSONAS.BANK]: {
        title: 'Enterprise Integration Hub',
        what: 'API keys, webhooks, health monitoring, and ops recovery dashboard for bank system integrations.',
        why: 'Secure integrations with operational visibility. Webhook health indicators and retry controls enable proactive incident response.',
        who: 'Bank tech teams configure integrations; ops monitors health and recovers from failures without engineering escalation.',
        next: 'Configure integrations, monitor webhook health, and explore the retry dashboard for ops resilience.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Integration Capabilities',
        what: 'Secure APIs, real-time webhooks, and built-in observability for connecting your systems to Granullo.',
        why: 'Automated workflows with proactive monitoring. See integration health and understand operational recovery processes.',
        who: 'Technical teams manage keys; operations receives notifications and understands reliability guarantees.',
        next: 'Explore integrations and understand how health metrics and ops recovery enhance operational confidence.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'API Keys, Webhooks & Ops Recovery',
        what: 'Full lifecycle management with usage analytics, webhook health indicators, and retry dashboard for failed deliveries.',
        why: 'Enterprise-grade security + operational resilience. Identify issues, understand backoff stages, recover safely.',
        who: 'Developers manage keys/endpoints. Ops monitors analytics, health badges, and retry dashboard for incident recovery.',
        next: 'View API usage analytics, check webhook health, and explore the retry dashboard with backoff visualization.'
      }
    }
  },

  'csr-programs': {
    id: 'csr-programs',
    targetPath: '/csr/programs',
    navItem: 'CSR Programs',
    requiredRoles: ['super_admin', 'bank_partner', 'regulator', 'auditor', 'client_admin'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'CSR Program Registry • Budget & Refund Governance',
        what: 'Corporate CSR programs with defined budgets, refund deadlines, and allocation rules.',
        why: 'Programs enforce governance guardrails before any fund allocation. Refund rules ensure accountability.',
        who: 'Regulators verify program configurations, refund policies, and budget utilization rates.',
        next: 'Review active programs, refund deadlines, and allocation summaries for compliance assessment.'
      },
      [PERSONAS.BANK]: {
        title: 'CSR Programs • Corporate Fund Intent',
        what: 'Corporate CSR programs that define budgets, approved partners, and disbursement rules.',
        why: 'Understanding program structure helps anticipate allocation volumes and refund scenarios.',
        who: 'Bank partners review program activation, monitor refund triggers, and track utilization.',
        next: 'Explore program configurations, refund rules, and allocation pipelines.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Your CSR Programs • Fund Management',
        what: 'Create and manage CSR programs with budgets, cause categories, and refund deadlines.',
        why: 'Programs are the foundation of your CSR execution. Define rules before allocating funds.',
        who: 'Corporate CSR admins create programs; funds flow to verified foundations through allocations.',
        next: 'Click "Create CSR Program" to define a new program or review existing program utilization.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'CSR Programs • API Objects',
        what: 'Program resources with budgets, allocation rules, refund configurations, and status lifecycles.',
        why: 'Programs are parent objects for all CSR operations. Understanding the schema enables automation.',
        who: 'Developers building CSR dashboards, program management tools, or refund monitoring.',
        next: 'Review Program API for creation, activation, and allocation summary endpoints.'
      }
    }
  },

  'csr-partners': {
    id: 'csr-partners',
    targetPath: '/csr/partners',
    navItem: 'CSR Partners',
    requiredRoles: ['super_admin', 'bank_partner', 'regulator', 'auditor'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'CSR Execution Partners • Verification & Auditability',
        what: 'Registry of verified foundations and charities authorized for CSR fund disbursement.',
        why: 'Every CSR partner undergoes structured verification. Evidence is immutable and audit-ready.',
        who: 'Regulators verify partner categorization, risk posture, and evidence integrity.',
        next: 'Review partner verification history, evidence documents, and clarification threads.'
      },
      [PERSONAS.BANK]: {
        title: 'CSR Execution Partners • Trust & Verification',
        what: 'Verified foundations and charities that corporates depend on for CSR fund allocation.',
        why: 'Granullo\'s verification creates long-term corporate dependency for CSR execution.',
        who: 'Bank partners review partner maturity, risk flags, and operational capabilities.',
        next: 'Explore verified partners, pending applications, and attention-needed items.'
      },
      [PERSONAS.CLIENT]: {
        title: 'CSR Partners • Execution Network',
        what: 'Network of verified foundations and charities available for CSR disbursement programs.',
        why: 'Pre-verified partners reduce due diligence burden and ensure compliant fund flows.',
        who: 'Program administrators select partners; compliance teams verify categorization.',
        next: 'Browse partners by cause category, operating region, and risk posture.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'CSR Partners • API Resources',
        what: 'Partner profiles, applications, evidence documents, and verification history as API objects.',
        why: 'Structured data for building CSR allocation integrations and compliance reporting.',
        who: 'Developers building CSR dashboards, partner selection workflows, or audit automation.',
        next: 'Review partner API structure, filter parameters, and evidence schemas.'
      }
    }
  },

  'csr-allocations': {
    id: 'csr-allocations',
    targetPath: '/csr/allocations',
    navItem: 'CSR Allocations',
    requiredRoles: ['super_admin', 'bank_partner', 'regulator', 'auditor'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'CSR Allocation Tracking • Governance & Health Signals',
        what: 'Corporate CSR fund allocations with lifecycle tracking, health signals (on-track/at-risk/delayed), and evidence coverage.',
        why: 'Every allocation is traceable from commitment to utilisation. Health signals flag delays proactively. Evidence is immutable and audit-ready.',
        who: 'Regulators verify allocation integrity, monitor health signals, and review evidence completeness.',
        next: 'Review health distribution, active alerts, and allocation audit surface for compliance posture.'
      },
      [PERSONAS.BANK]: {
        title: 'CSR Allocation Tracking • Governance Controls & Signals',
        what: 'Track allocations through commitment, release, utilisation, and closure with proactive health monitoring.',
        why: 'Health signals surface at-risk allocations before deadlines. Alerts notify governance teams of stalled utilisation or evidence gaps.',
        who: 'Bank partners approve releases, monitor health signals, and resolve allocation alerts.',
        next: 'Review Health Signals tab, approve pending releases, and address active alerts.'
      },
      [PERSONAS.CLIENT]: {
        title: 'CSR Allocations • Fund Accountability & Monitoring',
        what: 'Track CSR budget allocations with health signals showing on-track, at-risk, and delayed initiatives.',
        why: 'Proactive monitoring ensures funds flow to partners on schedule. Alerts highlight evidence gaps and utilisation delays.',
        who: 'Corporate CSR teams monitor allocation health; partners report utilisation; platform flags exceptions.',
        next: 'View Health Signals tab, track allocation progress, and respond to active alerts.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'CSR Allocations • API & Health Monitoring',
        what: 'Allocation lifecycle, health signals (on_track/at_risk/delayed/blocked), alerts, and notes as API resources.',
        why: 'Health APIs enable proactive dashboards. Notes API supports allocation-scoped clarification threads.',
        who: 'Developers building CSR monitoring dashboards, alert integrations, or governance workflows.',
        next: 'Review /health/overview, /alerts/active, and /{id}/notes endpoints for v0.2 capabilities.'
      }
    }
  },

  csr_governance_map: {
    id: 'csr_governance_map',
    targetPath: '/governance/why-csr-breaks',
    navItem: 'Why CSR Breaks',
    requiredRoles: ['super_admin', 'csr_admin', 'client_admin', 'bank_partner', 'regulator', 'auditor'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'CSR Failure Mode Registry • Regulator Reference',
        what: 'Deterministic map of how CSR initiatives fail in the real world and how Granullo governance prevents each failure mode.',
        why: 'Regulators need clear understanding of systemic risks. This map links each failure to specific governance controls that block it.',
        who: 'Regulators reference during audits to verify governance coverage. Maps directly to compliance requirements.',
        next: 'Click each failure mode to see: real-world examples, Granullo enforcement rules, and system component mappings.'
      },
      [PERSONAS.BANK]: {
        title: 'CSR Failure Prevention • Operational Reference',
        what: 'Understanding how CSR fund flows fail without governance and how Granullo execution controls prevent issues.',
        why: 'Bank partners verify that disbursement requests meet governance requirements before execution.',
        who: 'Operations teams use this to understand why certain requests are blocked and what resolution is needed.',
        next: 'Review failure modes related to merchant fraud, delayed utilisation, and audit trail gaps.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Why CSR Breaks • And How Granullo Prevents It',
        what: 'A governance reference showing common CSR failure modes and specific controls that preserve accountability.',
        why: 'Understanding governance prevents surprises. When actions are blocked, this explains why and how to resolve.',
        who: 'CSR teams, donors, and program administrators reference to understand platform guardrails.',
        next: 'Explore failure modes like "Funds Not Disbursed", "Merchant Fraud", and "Delayed Utilisation".'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Governance Rule Engine • System Reference',
        what: 'Mapping of failure modes to system states, blocking rules, and API enforcement points.',
        why: 'Developers building integrations need to understand which actions trigger governance blocks.',
        who: 'Technical teams reference when debugging blocked operations or building governance dashboards.',
        next: 'Note system state mappings and blocking rule implementations for API integration.'
      }
    }
  },

  csr_analytics: {
    id: 'csr_analytics',
    targetPath: '/csr/analytics',
    navItem: 'CSR Analytics',
    requiredRoles: ['super_admin', 'csr_admin', 'client_admin', 'bank_partner', 'regulator', 'auditor'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'CSR Execution Analytics • Regulator Dashboard',
        what: 'Operational metrics: fund states (blocked/active/refunded), time-to-utilisation, blocking reasons, refund risk analysis.',
        why: 'Regulators verify execution compliance. Blocked vs Active vs Refunded breakdown shows real fund flow status.',
        who: 'Auditors use for compliance verification. Regulators track sector-wide execution health.',
        next: 'Review Execution Status tab, check blocking breakdown, and verify refund risk allocations.'
      },
      [PERSONAS.BANK]: {
        title: 'CSR Execution Analytics • Operations View',
        what: 'Fund flow analytics: what is blocked, why it is blocked, which allocations face refund deadlines.',
        why: 'Bank partners need visibility into execution pipeline to plan operations and resolve blocks.',
        who: 'Operations teams identify bottlenecks; managers forecast execution volumes.',
        next: 'Check Execution Status for blocked funds, then Refund Risk for deadline approaching allocations.'
      },
      [PERSONAS.CLIENT]: {
        title: 'CSR Execution Dashboard • CFO/Donor View',
        what: 'Your CSR execution analytics: how funds are flowing, what is blocked, and what requires attention.',
        why: 'CFO-readable view of CSR effectiveness. No vanity metrics • operational execution data only.',
        who: 'CFOs track execution efficiency; donors verify fund utilisation; CSR teams identify blockers.',
        next: 'Start with Execution Status to see fund breakdown, then check Refund Risk for at-risk allocations.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'CSR Analytics APIs • Execution Metrics',
        what: 'Analytics endpoints: /execution-overview, /allocation-health-states, /refund-risk-analysis.',
        why: 'Build executive dashboards with real execution data. All metrics are auditable and derived from ledger.',
        who: 'Developers building CSR reporting dashboards or governance alert systems.',
        next: 'Review execution-overview for fund states, refund-risk-analysis for deadline tracking.'
      }
    }
  },

  csr_invites: {
    id: 'csr_invites',
    targetPath: '/csr/invites',
    navItem: 'Foundation Invites',
    requiredRoles: ['super_admin', 'csr_admin', 'client_admin'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'Foundation Invite Registry • Audit Trail',
        what: 'Record of all foundation invitations: who invited, reason for invite, acceptance status.',
        why: 'Audit trail for foundation onboarding. Every invite requires justification for compliance.',
        who: 'Regulators verify that foundations joined through controlled, documented channels.',
        next: 'Review invite reasons, check for duplicate detection alerts, verify invite → partner chain.'
      },
      [PERSONAS.BANK]: {
        title: 'Foundation Invites • Partner Pipeline',
        what: 'Track corporate invitations to foundations. Invites include reason, program context, and expiry.',
        why: 'Bank partners can verify that foundations were vetted before onboarding. Part of partner due diligence.',
        who: 'Compliance teams verify invitation justifications align with CSR program requirements.',
        next: 'Review pending invites, check duplicate detection alerts, verify invite chain.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Invite Trusted Foundations',
        what: 'Send controlled invitations to foundations you want to partner with. Each invite requires a reason.',
        why: 'Foundations can only join via invite or public application. Invites provide vetted onboarding path.',
        who: 'Corporate CSR teams invite foundations; foundations accept and complete registration.',
        next: 'Click "New Invitation" to invite a foundation. Include program context if relevant.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Foundation Invite API',
        what: 'Invite creation, duplicate detection, acceptance flow, and status tracking APIs.',
        why: 'Integrate foundation onboarding into corporate systems. Duplicate detection prevents fraud.',
        who: 'Developers building CSR management portals or automated onboarding flows.',
        next: 'Review /invite POST for creation, /invite/{token} GET for public acceptance flow.'
      }
    }
  },

  csr_communication: {
    id: 'csr_communication',
    targetPath: '/csr/communication',
    navItem: 'Communication',
    requiredRoles: ['super_admin', 'csr_admin', 'client_admin', 'bank_partner', 'regulator', 'auditor'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'Threaded Communication • Compliance Record',
        what: 'Auditable communication threads between Clients and Foundations. Bank/Regulator has read-only access.',
        why: 'Every clarification request and response is recorded. Resolved threads are immutable.',
        who: 'Regulators review communication history for compliance. Bank partners monitor blocking threads.',
        next: 'Filter by "Blocking" to see threads that are holding up fund execution.'
      },
      [PERSONAS.BANK]: {
        title: 'Communication Threads • Execution Blocks',
        what: 'Monitor Client ↔ Foundation communication. Clarification threads can block disbursements.',
        why: 'Understanding blocking reasons helps resolve execution delays. Read-only visibility for oversight.',
        who: 'Bank compliance reviews threads related to blocked allocations.',
        next: 'Check "Awaiting Response" threads to understand what is blocking fund release.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Communication with Foundations',
        what: 'Structured communication for clarifications, document requests, and execution coordination.',
        why: 'Clarification requests block actions until resolved. Keeps communication auditable, not ad-hoc.',
        who: 'CSR teams raise clarifications; Foundations respond; both parties can attach documents.',
        next: 'Create a thread for any clarification needed. Use "Document Request" type for missing docs.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Communication Thread API',
        what: 'Threaded notes with blocking logic, status transitions, and attachment support.',
        why: 'Blocking threads integrate with governance layer. Status changes affect allocation actions.',
        who: 'Developers building clarification workflows or notification systems.',
        next: 'Review /threads POST for creation, /{id}/status PUT for resolution.'
      }
    }
  },

  csrHub: {
    id: 'csr_hub',
    targetPath: '/csr/start',
    navItem: 'CSR Hub',
    requiredRoles: ['super_admin', 'client_admin', 'bank_partner', 'regulator', 'auditor', 'donor'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'CSR Hub • Entry Control',
        what: 'Central entry point for CSR governance. Four distinct paths: Corporate, Donor, Foundation, Oversight.',
        why: 'Clear access control prevents role confusion. Every actor enters through the correct path.',
        who: 'Regulators verify that access boundaries are respected and roles are clearly communicated.',
        next: 'Review each path definition. Verify that your oversight access is correctly scoped.'
      },
      [PERSONAS.BANK]: {
        title: 'CSR Hub • Operational Entry',
        what: 'Starting point for CSR fund flow management. Choose your role-specific entry path.',
        why: 'Bank partners access oversight views without program creation capabilities.',
        who: 'Bank compliance and operations teams access allocation monitoring from here.',
        next: 'Select "Access Oversight View" to enter the read-only allocation monitoring surface.'
      },
      [PERSONAS.CLIENT]: {
        title: 'CSR Hub • Your Starting Point',
        what: 'Gateway to Granullo CSR governance. Corporates create programs; Donors view; Foundations apply.',
        why: 'Clear role definition prevents confusion. Know what you can do before you start.',
        who: 'Corporate CSR heads start programs. Individual donors track governed initiatives.',
        next: 'Corporate: "Start / Manage CSR Programs". Donor: "Contribute via Governed CSR".'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'CSR Hub • Entry API Surface',
        what: 'Role-aware entry routing for CSR features. Four distinct user journeys.',
        why: 'Frontend routing logic mirrors backend RBAC. Role determines available actions.',
        who: 'Developers implementing role-based navigation and access control.',
        next: 'Review route guards and role checks in Layout.js and CSRHub.jsx.'
      }
    }
  },

  impactIntelligence: {
    id: 'impact_intelligence',
    targetPath: '/impact-intelligence',
    navItem: 'Impact Intelligence',
    requiredRoles: ['super_admin', 'client_admin', 'bank_partner', 'regulator', 'auditor', 'donor'],
    copy: {
      [PERSONAS.RBI]: {
        title: 'Impact Intelligence • Factual Context',
        what: 'Read-only cause exploration with source-attributed data. No rankings. No recommendations.',
        why: 'Granullo informs decisions. Granullo does not choose causes. Context before commitment.',
        who: 'Regulators verify that no recommendation logic exists. All data is factual and attributed.',
        next: 'Review Active Alerts to see situational awareness surface. Check that disclaimers are visible.'
      },
      [PERSONAS.BANK]: {
        title: 'Impact Intelligence • Situational Awareness',
        what: 'Factual data on causes and regions. Active alerts for disasters, epidemics, conflicts.',
        why: 'Bank partners need context for allocation decisions. No urgency CTAs. No donation prompts.',
        who: 'Bank compliance reviews cause data before approving large allocations.',
        next: 'Filter by region to see relevant data. Review Active Alerts for blocking considerations.'
      },
      [PERSONAS.CLIENT]: {
        title: 'Impact Intelligence • Inform Your Decisions',
        what: 'Explore causes, regions, and active situations. All data is factual and source-attributed.',
        why: 'Make informed CSR decisions based on facts, not marketing. Save references for planning.',
        who: 'Corporate CSR heads and donors research causes before committing funds.',
        next: 'Use filters to explore. Save to Planning Notes for internal reference.'
      },
      [PERSONAS.DEVELOPER]: {
        title: 'Impact Intelligence • Data Architecture',
        what: 'Static data layer with filters, snippets, alerts, and planning notes.',
        why: 'Demonstrates read-only module design. No recommendation engine. No ML scoring.',
        who: 'Developers reviewing read-only data surface patterns.',
        next: 'Review API at /api/csr/impact-intelligence/*. Check planning notes persistence.'
      }
    }
  }
};

/**
 * DEVELOPER GUIDED VIEW • Separate 5-step technical flow
 * Only visible to: super_admin, bank_tech, rbi_tech
 */
export const DEVELOPER_GUIDED_FLOW = {
  'dev-api-keys': {
    id: 'dev-api-keys',
    targetPath: '/developers',
    navItem: 'API Keys',
    copy: {
      title: 'API Key Lifecycle',
      what: 'Create, rotate, and revoke API keys for programmatic access to Granullo.',
      why: 'Secure API access is foundational. Keys are scoped, rotatable, and auditable.',
      who: 'Technical leads manage keys; security teams audit access patterns.',
      next: 'Create a test key to explore the API, or review existing key usage.',
      phase: 'Available now'
    }
  },
  'dev-webhooks': {
    id: 'dev-webhooks',
    targetPath: '/developers',
    navItem: 'Webhooks',
    copy: {
      title: 'Webhook Event Flow',
      what: 'Subscribe to real-time events for approvals, executions, and status changes.',
      why: 'Push-based integration reduces polling and enables reactive workflows.',
      who: 'Developers configure endpoints; operations teams monitor delivery.',
      next: 'Register a webhook endpoint and review the event catalog.',
      phase: 'Available now'
    }
  },
  'dev-signing': {
    id: 'dev-signing',
    targetPath: '/developers',
    navItem: 'Signing & Verification',
    copy: {
      title: 'Request Signing & Verification',
      what: 'Cryptographic signing of requests and verification of webhook payloads.',
      why: 'Ensures request integrity and authenticity. Non-repudiation for audit.',
      who: 'Security engineers implement; compliance teams verify.',
      next: 'Review signing documentation and test verification endpoints.',
      phase: 'Enterprise Integrations • Phase 2'
    }
  },
  'dev-retry': {
    id: 'dev-retry',
    targetPath: '/developers',
    navItem: 'Retry & Recovery',
    copy: {
      title: 'Retry Logic & Failure Handling',
      what: 'Automatic retries, dead letter queues, and manual replay capabilities.',
      why: 'Resilient integrations handle failures gracefully without data loss.',
      who: 'Operations teams configure; developers implement handlers.',
      next: 'Review retry policies and configure failure notifications.',
      phase: 'Enterprise Integrations • Phase 2'
    }
  },
  'dev-environments': {
    id: 'dev-environments',
    targetPath: '/developers',
    navItem: 'Environments',
    copy: {
      title: 'Demo vs Production Environments',
      what: 'Isolated environments for testing, staging, and production operations.',
      why: 'Safe development without affecting live operations or data.',
      who: 'All technical roles work across environments with appropriate access.',
      next: 'Understand environment separation and data isolation policies.',
      phase: 'Available now'
    }
  }
};

/**
 * Get Developer Guided Flow steps
 */
export const getDeveloperGuidedSteps = () => {
  return Object.values(DEVELOPER_GUIDED_FLOW);
};

/**
 * Check if user can access Developer Guided View
 */
export const canAccessDeveloperGuide = (userRole) => {
  return ['super_admin', 'founder_superadmin', 'bank_tech', 'rbi_tech'].includes(userRole);
};

/**
 * Complete step order (18 steps • includes Trust surfaces + Enterprise Integrations + CSR v3.0)
 * Trust and CSR steps are role-gated and auto-skipped for non-permitted roles
 */
export const STEP_ORDER = [
  'dashboard',
  'programs',
  'program-builder',
  'beneficiaries',
  'merchants',
  'policies',
  'approvals',
  'bank-requests',
  'review-clarifications',
  'compliance-audit',
  'trust-pilot',
  'executive-dashboard',
  'enterprise-integrations',
  'csr-programs',
  'csr-partners',
  'csr-allocations'
];

/**
 * Get steps filtered by user role (smart-skip role-gated steps)
 */
export const getStepsForRole = (userRole) => {
  return STEP_ORDER.filter(stepId => {
    const step = GUIDED_COPY_MATRIX[stepId];
    if (!step.requiredRoles) return true;
    return step.requiredRoles.includes(userRole);
  });
};

/**
 * Get copy for a specific step and persona
 */
export const getStepCopy = (stepId, persona = PERSONAS.CLIENT) => {
  const step = GUIDED_COPY_MATRIX[stepId];
  if (!step) return null;
  
  // Fallback to CLIENT if persona copy not available
  const copy = step.copy[persona] || step.copy[PERSONAS.CLIENT];
  return {
    id: step.id,
    targetPath: step.targetPath,
    navItem: step.navItem,
    requiredRoles: step.requiredRoles,
    ...copy
  };
};

/**
 * Get all steps with copy for a persona (unfiltered)
 */
export const getAllStepsForPersona = (persona = PERSONAS.CLIENT) => {
  return STEP_ORDER.map(stepId => getStepCopy(stepId, persona));
};

/**
 * Playback modes
 */
export const PLAYBACK_MODES = {
  MANUAL: 'manual',
  AUTO: 'auto'
};

/**
 * Auto playback speeds (ms per step) • FASTER for demo
 */
export const PLAYBACK_SPEEDS = {
  slow: { label: 'Slow (6s)', ms: 6000 },
  normal: { label: 'Normal (4s)', ms: 4000 },
  fast: { label: 'Fast (2.5s)', ms: 2500 }
};

/**
 * LocalStorage keys namespace
 */
export const STORAGE_KEYS = {
  ENABLED: 'granullo.guided.enabled',
  MODE: 'granullo.guided.mode',
  PERSONA: 'granullo.guided.persona',
  STEP_INDEX: 'granullo.guided.stepIndex',
  SPEED: 'granullo.guided.speed',
  LOOP: 'granullo.guided.loop'
};
